<template>
	<section class="review-page w-full max-w-6xl mx-auto px-0">
		<header class="review-hero border-b border-defaultborder pb-6 mb-8">
			<p class="review-kicker">{{ texts.kicker }}</p>
			<h1 class="mt-2">{{ config.booking.event_name }}</h1>
			<p class="review-intro">{{ texts.globalDescription }}</p>
			<div class="review-pills">
				<span v-for="pill in reviewPills" :key="pill.value" class="review-pill">
					<strong>{{ pill.label }}</strong>
					<span>{{ pill.value }}</span>
				</span>
			</div>
		</header>

		<div class="grid gap-8 lg:grid-cols-5">
			<div class="lg:col-span-3 space-y-8">
				<section class="review-panel">
					<div class="review-panel-head">
						<h2>{{ texts.ratingTitle }}</h2>
						<p>{{ texts.ratingDescription }}</p>
					</div>
					<div class="rating-list">
						<template v-for="{id, code} in config.ratings" :key="id">
							<div class="review-rating-row">
								<div class="review-rating-label">
									<label>{{ code }}</label>
								</div>
								<div class="review-rating-control">
									<star-rating-form-field
											:model-value="rawRatings[id]"
											@update:model-value="updateRatingValue(id, $event)"
									/>
								</div>
							</div>
						</template>
						<ErrorMessagesDisplay :error="error('rawRatings')"></ErrorMessagesDisplay>
					</div>
				</section>

				<section class="review-panel">
					<div class="review-panel-head">
						<h2>{{ texts.personalReviewTitle }}</h2>
						<p>{{ texts.personalReviewDescription }}</p>
					</div>
					<div class="review-fieldrow">
						<label>{{ texts.titleLabel }}</label>
						<input
								v-model="form.title"
								:placeholder="texts.titlePlaceholder"
								class="w-full"
								type="text"
						>
						<ErrorMessagesDisplay :error="error('title')"></ErrorMessagesDisplay>
					</div>
					<div class="review-fieldrow">
						<label>{{ texts.detailLabel }}</label>
						<textarea
								v-model="form.detail"
								:placeholder="texts.detailPlaceholder"
								class="w-full"
								rows="6"
						/>
						<ErrorMessagesDisplay :error="error('detail')"></ErrorMessagesDisplay>
					</div>
					<div class="review-fieldrow">
						<label>{{ texts.nicknameLabel }}</label>
						<input
								v-model="form.nickname"
								:placeholder="texts.nicknamePlaceholder"
								class="w-full"
								type="text"
						>
						<ErrorMessagesDisplay :error="error('nickname')"></ErrorMessagesDisplay>
					</div>
					<div class="review-fieldrow review-submit-row">
						<button
								:disabled="submitting" class="w-full button button-orange text-lg sm:text-xl"
								@click="submit">{{ texts.submitButtonLabel }}
						</button>
					</div>
				</section>
			</div>

			<aside class="review-aside lg:col-span-2">
				<div class="review-aside-panel">
					<p class="review-aside-kicker">{{ texts.asideKicker }}</p>
					<h2 class="mt-2">{{ texts.asideTitle }}</h2>
					<p class="mt-3">{{ texts.asideDescription }}</p>
					<dl class="review-meta-list">
						<div>
							<dt>{{ texts.eventLabel }}</dt>
							<dd>{{ config.booking.event_name }}</dd>
						</div>
						<div v-if="config.booking.organization">
							<dt>{{ texts.organizationLabel }}</dt>
							<dd>{{ config.booking.organization }}</dd>
						</div>
					</dl>
					<ul class="review-tip-list">
						<li>{{ texts.tipOne }}</li>
						<li>{{ texts.tipTwo }}</li>
						<li>{{ texts.tipThree }}</li>
					</ul>
				</div>
			</aside>
		</div>
	</section>
</template>

<script lang="ts">
import {defineComponent, PropType} from 'vue'
import useVuelidate from '@vuelidate/core'
import {ReviewConfigInterface, ReviewFormInterface} from '@/js/interfaces/review'
import StarRatingFormField from "@/js/components/ui/StarRatingFormField.vue"
import {helpers, minLength, required} from "@vuelidate/validators"
import {addReview} from "@/js/api/backendSp"
import {useFieldError} from "@/js/composables/vuelidate"
import ErrorMessagesDisplay from "@/js/components/ui/ErrorMessagesDisplay.vue"

const texts = {
	kicker: 'Seminarbewertung',
	globalDescription: 'Sie haben diese Veranstaltung kürzlich besucht. Ihre Rückmeldung hilft anderen Teilnehmenden bei der Auswahl passender Seminare und unterstützt uns bei der Qualitätssicherung und bei der Optimierung unseres Angebots.',
	ratingTitle: 'Ihre Benotung der Veranstaltung',
	ratingDescription: 'Welche Bewertung würden Sie den folgenden Aspekten der Veranstaltung geben?',
	personalReviewTitle: 'Ihre persönliche Bewertung',
	personalReviewDescription: 'Bitte geben Sie hier Ihre persönliche Bewertung der Veranstaltung ein. Auch wenn Sie das Seminar bereits vor Ort beurteilt haben: Diese Rückmeldung wird zusätzlich für unsere Plattform verwendet.',
	titleLabel: 'Titel Ihrer Rückmeldung',
	titlePlaceholder: 'Zum Beispiel: Sehr praxisnah und gut organisiert',
	detailLabel: 'Ihre persönliche Rückmeldung',
	detailPlaceholder: 'Ein paar Sätze reichen. Was war hilfreich? Wie schätzen Sie den Nutzen der Teilnahme für Personen in Ihrer Funktion ein?',
	nicknameLabel: 'Name oder Fantasiename und wenn möglich Funktion',
	nicknamePlaceholder: 'So möchten wir Ihre Rückmeldung anzeigen',
	submitButtonLabel: 'Bewertung absenden',
	asideKicker: 'Warum das hilft',
	asideTitle: 'Eine kurze Rückmeldung reicht',
	asideDescription: 'Sie müssen keinen langen Text schreiben. Schon eine ehrliche Einschätzung hilft anderen bei der Seminarauswahl und uns bei der Qualitätssicherung und Optimierung des Angebots.',
	eventLabel: 'Veranstaltung',
	organizationLabel: 'Veranstalter',
	tipOne: 'Nennen Sie ruhig auch einzelne Punkte, die besonders gut waren.',
	tipTwo: 'Wenn etwas gefehlt hat, sagen Sie es direkt und konkret.',
	tipThree: 'Ein Fantasiename ist ebenfalls in Ordnung.',
}
const messages = {
	ratingsRequired: 'Bitte geben Sie eine Bewertung für alle Aspekte der Veranstaltung an.',
	required: 'Dieses Feld ist ein Pflichtfeld.',
	validationError: 'Da scheint etwas noch nicht ganz zu stimmen. Bitte prüfen Sie die Eingabefelder...',
	submissionError: 'Bei der Übermittlung Ihrer Bewertung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
}

const ratingsMustBeFilled = (ratings) => {
	for (const rating in ratings) {
		if (ratings[rating] === 0) {
			return false
		}
	}
	return true
}

export default defineComponent({
	name: 'EventReviewForm',
	components: {StarRatingFormField, ErrorMessagesDisplay},
	setup: () => ({v$: useVuelidate()}),
	props: {
		config: {
			type: Object as PropType<ReviewConfigInterface>,
			required: true,
		},
	},
	data() {
		return {
			texts: texts,
			form: {
				form_key: this.config.formKey,
				id: this.config.booking.event_id,
				ratings: {},
				detail: '',
				title: '',
				nickname: this.config.booking.name + (this.config.booking.job ? ' (' + this.config.booking.job + ')' : ''),
				review_token: this.config.reviewQueueItem.token
			} as ReviewFormInterface,
			rawRatings: {},
			submitting: false,
		}
	},
	mounted() {
		this.config.ratings.forEach(rating => {
			this.rawRatings[rating.id] = 0
		})
	},
	methods: {
		error(prop: string) {
			return useFieldError(prop, this.errors)
		},
		updateRatingValue(id: number, value: number) {
			this.rawRatings[id] = value
		},
		getRatingById(id: number) {
			return this.config.ratings.filter(rating => {
				return rating.id === id
			})[0]
		},
		getRatingOptionIdByValue(id: number, value: number) {
			return this.getRatingById(id).options.filter(option => {
				return option.value === value
			})[0].id
		},
		replaceRatingValues() {
			this.form.ratings = {}
			for (const rating in this.rawRatings) {
				this.form.ratings[rating] = this.getRatingOptionIdByValue(parseInt(rating), this.rawRatings[rating])
			}
		},
		async submit() {
			this.submitting = true
			const result = await this.v$.$validate()
			if (result) {
				try {
					this.replaceRatingValues()
					const data = await addReview(this.form)
					this.$toast.success(data)
					this.submitting = false
					setTimeout(() => {
						window.location.href = '/'
					}, 5000)
				} catch (e: any) {
					if (e.response && e.response.data) {
						this.$toast.error(e.response.data)
					} else {
						this.$toast.error(messages.submissionError)
					}
				} finally {
					this.submitting = false
				}
			} else {
				this.$toast.error(messages.validationError)
				this.submitting = false
			}
		}
	},
	computed: {
		errors() {
			return this.v$.$errors
		},
		reviewPills() {
			return [
				{label: 'Dauer', value: 'ca. 2 Minuten'},
				{label: 'Hinweis', value: 'Fantasiename möglich'},
				{label: 'Zweck', value: 'hilft direkt weiter'},
			]
		},
	},
	validations() {
		return {
			form: {
				title: {
					required: helpers.withMessage(messages.required, required),
					minLength: helpers.withMessage(({$params}) => `Bitte geben Sie mindestens ${$params.min} Zeichen ein.`, minLength(5)),
				},
				detail: {
					required: helpers.withMessage(messages.required, required),
					minLength: helpers.withMessage(({$params}) => `Bitte geben Sie mindestens ${$params.min} Zeichen ein.`, minLength(10)),
				},
				nickname: {
					required: helpers.withMessage(messages.required, required),
					minLength: helpers.withMessage(({$params}) => `Bitte geben Sie mindestens ${$params.min} Zeichen ein.`, minLength(3)),
				}
			},
			rawRatings: {
				ratingsMustBeFilled: helpers.withMessage(messages.ratingsRequired, ratingsMustBeFilled),
			}
		}
	},

})
</script>

<style scoped>
.review-page {
	@apply mx-auto w-full px-0;
}

.review-hero {
	@apply mb-8;
}

.review-kicker,
.review-aside-kicker {
	@apply text-xs font-semibold uppercase tracking-wide text-primary;
}

.review-intro {
	@apply mt-4 max-w-3xl text-base sm:text-lg text-gray-700;
}

.review-pills {
	@apply mt-5 flex flex-wrap gap-3;
}

.review-pill {
	@apply inline-flex flex-col border border-defaultborder px-3 py-2 text-sm bg-white;
}

.review-pill strong {
	@apply text-xs uppercase tracking-wide text-gray-500;
}

.review-pill span {
	@apply mt-1 text-gray-900;
}

.review-panel {
	@apply border border-defaultborder bg-white p-4 sm:p-6;
}

.review-panel-head {
	@apply mb-5;
}

.review-panel-head h2 {
	@apply mb-2 text-xl sm:text-2xl;
}

.review-panel-head p {
	@apply text-gray-700;
}

.rating-list {
	@apply flex flex-col divide-y divide-defaultborder;
}

.review-rating-row {
	@apply grid gap-4 py-4 sm:grid-cols-[minmax(0,1fr)_160px] sm:items-center;
}

.review-rating-label {
	@apply font-medium text-gray-900;
}

.review-rating-control {
	@apply sm:justify-self-end;
}

.review-fieldrow {
	@apply my-4;
}

.review-fieldrow label {
	display: block;
	@apply mb-2 font-medium text-gray-900;
}

.review-fieldrow input,
.review-fieldrow textarea {
	@apply w-full border border-defaultborder bg-white px-3 py-2;
}

.review-submit-row {
	@apply mt-6;
}

.review-aside {
	@apply lg:pt-2;
}

.review-aside-panel {
	@apply border border-defaultborder bg-gray-50 p-4 sm:p-6;
}

.review-aside-panel h2 {
	@apply text-xl sm:text-2xl;
}

.review-meta-list {
	@apply mt-6 space-y-4 text-sm;
}

.review-meta-list dt {
	@apply text-xs font-semibold uppercase tracking-wide text-gray-500;
}

.review-meta-list dd {
	@apply mt-1 text-gray-900;
}

.review-tip-list {
	@apply mt-6 space-y-3 pl-5 text-sm text-gray-700 list-disc;
}
</style>
